/**
 * Shared ⋮ action menu with portaled panel (avoids table/card overflow clipping).
 *
 * actions: [{ id, icon, label, onClick, disabled?, danger?, title?, dividerBefore? }]
 */
import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import './ActionMenu.css';

function ActionMenu({
  actions = [],
  disabled = false,
  title = 'More actions',
  ariaLabel,
  emptyLabel = 'View Only',
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [placement, setPlacement] = useState(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  const visibleActions = actions.filter((action) => action && action.show !== false);
  const firstDangerIndex = visibleActions.findIndex((action) => action.danger);

  const updatePlacement = useCallback(() => {
    const el = buttonRef.current;
    if (!el || !isOpen) {
      setPlacement(null);
      return;
    }
    const rect = el.getBoundingClientRect();
    const margin = 8;
    const viewportH = window.innerHeight;
    const approxMenuHeight = Math.min(320, 48 + visibleActions.length * 44);
    const spaceBelow = viewportH - rect.bottom - margin;
    const openUpward = spaceBelow < approxMenuHeight && rect.top > approxMenuHeight;
    setPlacement({
      top: openUpward ? rect.top - margin : rect.bottom + margin,
      right: Math.max(8, window.innerWidth - rect.right),
      transformOrigin: openUpward ? 'bottom right' : 'top right',
      transform: openUpward ? 'translateY(-100%)' : 'none',
    });
  }, [isOpen, visibleActions.length]);

  useLayoutEffect(() => {
    if (!isOpen) {
      setPlacement(null);
      return;
    }
    updatePlacement();
  }, [isOpen, updatePlacement]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (event) => {
      if (
        menuRef.current?.contains(event.target) ||
        buttonRef.current?.contains(event.target)
      ) {
        return;
      }
      setIsOpen(false);
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('scroll', updatePlacement, true);
    window.addEventListener('resize', updatePlacement);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('scroll', updatePlacement, true);
      window.removeEventListener('resize', updatePlacement);
    };
  }, [isOpen, updatePlacement]);

  if (visibleActions.length === 0) {
    return <span className="action-menu-empty">{emptyLabel}</span>;
  }

  const handleActionClick = (action) => {
    if (action.disabled) return;
    action.onClick?.();
    setIsOpen(false);
  };

  return (
    <div className="action-menu">
      <button
        ref={buttonRef}
        type="button"
        className="action-menu-btn"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen((open) => !open);
        }}
        title={title}
        aria-label={ariaLabel || title}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        disabled={disabled}
      >
        <span className="action-menu-btn-icon" aria-hidden="true">⋮</span>
      </button>

      {isOpen &&
        placement &&
        ReactDOM.createPortal(
          <div
            ref={menuRef}
            className="action-menu-panel"
            role="menu"
            style={{
              position: 'fixed',
              top: placement.top,
              right: placement.right,
              transform: placement.transform,
              transformOrigin: placement.transformOrigin,
              zIndex: 10050,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {visibleActions.map((action, index) => {
              const showDivider = action.dividerBefore || (action.danger && index === firstDangerIndex && index > 0);

              return (
                <React.Fragment key={action.id}>
                  {showDivider && <div className="action-menu-divider" aria-hidden="true" />}
                  <button
                    type="button"
                    role="menuitem"
                    className={`action-menu-item${action.danger ? ' action-menu-item--danger' : ''}`}
                    onClick={() => handleActionClick(action)}
                    disabled={action.disabled}
                    title={action.title || action.label}
                  >
                    {action.icon != null && (
                      <span className="action-menu-item-icon" aria-hidden="true">
                        {action.icon}
                      </span>
                    )}
                    <span className="action-menu-item-label">{action.label}</span>
                    {action.disabled && action.loading && (
                      <span className="action-menu-item-loading" aria-hidden="true">
                        ⏳
                      </span>
                    )}
                  </button>
                </React.Fragment>
              );
            })}
          </div>,
          document.body
        )}
    </div>
  );
}

export default ActionMenu;
